import { openDB } from 'idb';
import { DATA_VERSION } from './version';

export class DataManager {
  constructor(accountId) {
    this.modelsToSync = ['inbox', 'label', 'team'];
    this.accountId = accountId;
    this.db = null;
  }

  async initDb() {
    if (this.db) return this.db;
    const dbName = `cw-store-${this.accountId}`;
    this.db = await openDB(`cw-store-${this.accountId}`, DATA_VERSION, {
      upgrade(db) {
        db.createObjectStore('cache-keys');
        db.createObjectStore('inbox', { keyPath: 'id' });
        db.createObjectStore('label', { keyPath: 'id' });
        db.createObjectStore('team', { keyPath: 'id' });
      },
    });

    // Store the database name in LocalStorage
    const dbNames = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
    if (!dbNames.includes(dbName)) {
      dbNames.push(dbName);
      localStorage.setItem('cw-idb-names', JSON.stringify(dbNames));
    }

    return this.db;
  }

  validateModel(name) {
    if (!name) throw new Error('Model name is not defined');
    if (!this.modelsToSync.includes(name)) {
      throw new Error(`Model ${name} is not defined`);
    }
    return true;
  }

  // Clear + write in a SINGLE transaction. Doing it in two (and not awaiting
  // the clear) let WebKit close the clear's transaction before it ran, which
  // left the stale rows in place and made the following add() fail with
  // "Key already exists in the object store".
  async replace({ modelName, data }) {
    this.validateModel(modelName);

    const items = Array.isArray(data) ? data : [data];
    const tx = this.db.transaction(modelName, 'readwrite');
    await Promise.all([
      tx.store.clear(),
      ...items.map(item => tx.store.put(item)),
      tx.done,
    ]);
  }

  // put(), not add(): re-writing a row we already hold is an update, never a
  // constraint error.
  async push({ modelName, data }) {
    this.validateModel(modelName);

    const items = Array.isArray(data) ? data : [data];
    const tx = this.db.transaction(modelName, 'readwrite');
    await Promise.all([...items.map(item => tx.store.put(item)), tx.done]);
  }

  async get({ modelName }) {
    this.validateModel(modelName);
    return this.db.getAll(modelName);
  }

  async setCacheKeys(cacheKeys) {
    const tx = this.db.transaction('cache-keys', 'readwrite');
    await Promise.all([
      ...Object.keys(cacheKeys).map(modelName =>
        tx.store.put(cacheKeys[modelName], modelName)
      ),
      tx.done,
    ]);
  }

  async getCacheKey(modelName) {
    this.validateModel(modelName);

    return this.db.get('cache-keys', modelName);
  }
}
